import { useProjectOptions } from '../../../hooks/useProjects';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { sortRows } from '../../../utils/sortRows';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useSupplyOrders,
  useSupplyOrder,
  useSupplyOrderUnvaluedStock,
  useCreateSupplyOrder,
  useUpdateSupplyOrder,
  useIssueSupplyOrder,
  useMarkSupplyOrderSent,
  useAcknowledgeSupplyOrder,
  useReceiveSupplyOrder,
  useCancelSupplyOrder,
  useCloseSupplyOrder,
  usePrintSupplyOrder,
} from '../../../hooks/useSupplyOrders';
import { useProviders } from '../../../hooks/useProviders';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { useExternalOrders } from '../../../hooks/useExternalOrders';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';
import {
  ITEMS_PER_PAGE,
  INITIAL_FILTERS,
  getInitialSupplyOrderState,
} from '../constants/supplyOrdersConstants';
import {
  buildCreateSupplyOrderPayload,
  buildUpdateSupplyOrderPayload,
  buildProvidersByUuid,
  buildWarehousesByUuid,
  buildExternalOrdersByUuid,
  mapSupplyOrderToDisplay,
  mapSupplyOrderToFormState,
} from '../utils/supplyOrdersUtils';

const useDebouncedValue = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

export const useSupplyOrdersTable = () => {
  const tableSort = useTableSort();
  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useProjectOptions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(getInitialSupplyOrderState);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const debouncedQuery = useDebouncedValue(filters.query);

  const apiFilters = useMemo(
    () => ({
      ...filters,
      query: debouncedQuery,
    }),
    [filters, debouncedQuery]
  );

  const formModalOpen = dialogOpen || editDialogOpen;

  const { data: orders = [], isLoading: ordersLoading, refetch: refetchOrders } =
    useSupplyOrders(apiFilters);
  const { data: unvaluedStock = [] } = useSupplyOrderUnvaluedStock(selectedOrderId, {
    enabled: detailDialogOpen && Boolean(selectedOrderId),
  });
  const { data: detailOrder, isLoading: detailOrderLoading, refetch: refetchDetailOrder } =
    useSupplyOrder(selectedOrderId, {
      enabled: (detailDialogOpen || editDialogOpen) && Boolean(selectedOrderId),
    });
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses({
    enabled: formModalOpen || detailDialogOpen,
  });
  const { data: providers = [], isLoading: providersLoading } = useProviders({
    enabled: formModalOpen || detailDialogOpen || true,
  });
  const { data: externalOrders = [] } = useExternalOrders(undefined, {
    enabled: true,
  });

  const createMutation = useCreateSupplyOrder();
  const updateMutation = useUpdateSupplyOrder();
  const issueMutation = useIssueSupplyOrder();
  const markSentMutation = useMarkSupplyOrderSent();
  const acknowledgeMutation = useAcknowledgeSupplyOrder();
  const receiveMutation = useReceiveSupplyOrder();
  const cancelMutation = useCancelSupplyOrder();
  const closeMutation = useCloseSupplyOrder();
  const printMutation = usePrintSupplyOrder();

  const providersByUuid = useMemo(() => buildProvidersByUuid(providers), [providers]);
  const warehousesByUuid = useMemo(() => buildWarehousesByUuid(warehouses), [warehouses]);
  const externalOrdersByUuid = useMemo(
    () => buildExternalOrdersByUuid(externalOrders),
    [externalOrders]
  );

  const displayOrders = useMemo(
    () =>
      orders.map((order) =>
        mapSupplyOrderToDisplay(order, {
          providersByUuid,
          warehousesByUuid,
          externalOrdersByUuid,
        })
      ),
    [orders, providersByUuid, warehousesByUuid, externalOrdersByUuid]
  );

  const {
    data,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: sortRows(displayOrders, tableSort.sort),
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: (collection) => collection,
  });

  const selectedOrderDetail = useMemo(() => {
    if (!selectedOrderId || !detailOrder) return null;

    return mapSupplyOrderToDisplay(detailOrder, {
      providersByUuid,
      warehousesByUuid,
      externalOrdersByUuid,
    });
  }, [
    selectedOrderId,
    detailOrder,
    providersByUuid,
    warehousesByUuid,
    externalOrdersByUuid,
  ]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isProcessing =
    issueMutation.isPending ||
    markSentMutation.isPending ||
    acknowledgeMutation.isPending ||
    receiveMutation.isPending ||
    cancelMutation.isPending ||
    closeMutation.isPending ||
    printMutation.isPending;

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleOpenDialog = useCallback(() => {
    setSelectedOrder(getInitialSupplyOrderState());
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedOrder(getInitialSupplyOrderState());
  }, []);

  const handleOpenEditDialog = useCallback((order) => {
    setSelectedOrderId(order.uuid);
    setEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setSelectedOrderId(null);
    setSelectedOrder(getInitialSupplyOrderState());
  }, []);

  useEffect(() => {
    if (editDialogOpen && detailOrder) {
      const formState = mapSupplyOrderToFormState(detailOrder);
      if (formState) {
        setSelectedOrder(formState);
      }
    }
  }, [editDialogOpen, detailOrder]);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'destination_kind'
        ? { uuid_project: '', internal_reason: '', internal_reason_detail: '' } : {}),
      ...(name === 'internal_reason' ? { internal_reason_detail: '' } : {}),
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'source_type') {
        next.uuid_provider_legal_entity = '';
        next.uuid_source_warehouse = '';
        next.provider_delivery_commitment_at = '';
      }

      return next;
    });
  }, []);

  const handleAddMaterial = useCallback((material) => {
    if (!material?.value) return;

    setSelectedOrder((prev) => {
      if (prev.is_consolidated) return prev;
      const exists = (prev.materials || []).some(
        (item) => item.materialId === material.value
      );
      if (exists) return prev;

      return {
        ...prev,
        materials: [
          ...(prev.materials || []),
          {
            materialId: material.value,
            code: material.code,
            name: material.name,
            unit: material.unit,
            quantity: 1,
          },
        ],
      };
    });
  }, []);


  const handleUpdateMaterialQuantity = useCallback((materialId, quantity) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).map((item) =>
        (item.lineId || item.materialId) === materialId
          ? { ...item, quantity }
          : item
      ),
    }));
  }, []);

  const handleRemoveMaterial = useCallback((materialId) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).filter(
        (item) => (item.lineId || item.materialId) !== materialId
      ),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const payload = buildCreateSupplyOrderPayload(selectedOrder);
    if (!payload || !payload.items?.length) return false;

    await createMutation.mutateAsync(payload);
    handleCloseDialog();
    return true;
  }, [selectedOrder, createMutation, handleCloseDialog]);

  const handleUpdate = useCallback(async () => {
    const payload = buildUpdateSupplyOrderPayload(selectedOrder);
    if (!payload || !selectedOrderId || !payload.items?.length) return false;

    await updateMutation.mutateAsync({ id: selectedOrderId, data: payload });
    handleCloseEditDialog();
    return true;
  }, [selectedOrder, selectedOrderId, updateMutation, handleCloseEditDialog]);

  const handleViewDetail = useCallback((order) => {
    setSelectedOrderId(order.uuid);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleEditFromDetail = useCallback((order) => {
    setDetailDialogOpen(false);
    handleOpenEditDialog(order);
  }, [handleOpenEditDialog]);

  const handleRefreshDetailOrder = useCallback(async () => {
    await refetchDetailOrder();
    await refetchOrders();
  }, [refetchDetailOrder, refetchOrders]);

  return {
    projects,
    projectsLoading,
    projectsError,
    refetchProjects,
    sort: tableSort.sort,
    handleSort: (key) => { tableSort.toggleSort(key); handlePageChange(null, 1); },
    data,
    isLoading: ordersLoading,
    isSaving,
    isProcessing,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    filters,
    dialogOpen,
    editDialogOpen,
    detailDialogOpen,
    selectedOrder,
    selectedOrderDetail,
    detailOrderLoading,
    unvaluedStock,
    warehouses,
    providers,
    warehousesLoading,
    providersLoading,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleOpenDialog,
    handleCloseDialog,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleInputChange,
    handleSelectChange,
    handleAddMaterial,
    handleUpdateMaterialQuantity,
    handleRemoveMaterial,
    handleSave,
    handleUpdate,
    handleViewDetail,
    handleCloseDetailDialog,
    handleEditFromDetail,
    handleRefreshDetailOrder,
    issueMutation,
    markSentMutation,
    acknowledgeMutation,
    receiveMutation,
    cancelMutation,
    closeMutation,
    printMutation,
  };
};
